import { api } from "./client";
import { apiRoutes } from "util/apiRoutes";

/**
 * Actor descriptor used by promotion endpoints. `user` campaigns are
 * funded from the caller's personal balance; `team` campaigns are
 * funded from the team's actor balance and require the caller to be a
 * moderator of that team.
 */
export type PromotionSponsorActorType = "user" | "team";

/**
 * Promotion campaign as returned by `/api/promotions/*` endpoints. Keep
 * this shape in sync with `toCampaignResponse()` in
 * `backend/src/services/promotionService.js`.
 */
export interface PromotionCampaign {
  id: number;
  public_id: string;
  target_type: string;
  target_id: number;
  sponsor_actor_type: PromotionSponsorActorType | null;
  sponsor_actor_id: number | null;
  initiator_user_id: number | null;
  impressions_remaining: number;
  impressions_allocated: number;
  impressions_served: number;
  credits_staked_self: number;
  tokens_staked_self?: number;
  tokens_from_awards?: number;
  status: "active" | "paused" | "exhausted" | "expired" | "cancelled" | string;
  created_reason: "self_stake" | "award_trigger" | string;
  entity_title_cached: string | null;
  started_at: string;
  expires_at: string | null;
  last_served_at: string | null;
  last_boosted_at: string | null;
}

export interface StartCampaignInput {
  targetType: string;
  targetId: number;
  creditsStaked: number;
  sponsorActorType?: PromotionSponsorActorType;
  sponsorActorId?: number | null;
  idempotencyKey?: string | null;
}

export interface ListMyCampaignsInput {
  sponsorActorType?: PromotionSponsorActorType;
  sponsorActorId?: number | null;
}

/**
 * Error thrown by the promotions client when the server returns a 4xx/5xx
 * status with a structured error code. Callers should check `.code` to
 * pick the right UI copy.
 */
export class PromotionApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.name = "PromotionApiError";
    this.status = status;
    this.code = code;
  }
}

const rethrowAsPromotionError = (error: unknown, fallbackMessage: string): never => {
  if (error && typeof error === "object" && "isAxiosError" in error && (error as { isAxiosError?: boolean }).isAxiosError) {
    const axiosError = error as {
      response?: { status?: number; data?: { message?: string; code?: string } };
      message?: string;
    };
    const status = axiosError.response?.status ?? 500;
    const message = axiosError.response?.data?.message || axiosError.message || fallbackMessage;
    const code = axiosError.response?.data?.code || null;
    throw new PromotionApiError(status, message, code);
  }
  throw error;
};

export const promotionsApi = {
  /**
   * Start or extend a promotion campaign on a target.
   */
  startCampaign: async (input: StartCampaignInput): Promise<PromotionCampaign> => {
    try {
      const response = await api.post<PromotionCampaign>(apiRoutes.promotions.campaigns, {
        target_type: input.targetType,
        target_id: input.targetId,
        credits_staked: input.creditsStaked,
        sponsor_actor_type: input.sponsorActorType ?? "user",
        sponsor_actor_id: input.sponsorActorId ?? undefined,
        idempotency_key: input.idempotencyKey ?? undefined,
      });
      return response.data;
    } catch (error) {
      return rethrowAsPromotionError(error, "Failed to start promotion campaign.");
    }
  },

  /**
   * Get the active campaign for a target, if any.
   */
  getForTarget: async (entityType: string, entityId: number | string): Promise<PromotionCampaign | null> => {
    try {
      const response = await api.get<{ campaign: PromotionCampaign | null }>(
        apiRoutes.promotions.forTarget(entityType, entityId)
      );
      return response.data?.campaign ?? null;
    } catch (error) {
      return rethrowAsPromotionError(error, "Failed to load promotion campaign.");
    }
  },

  /**
   * List the caller's active campaigns. Defaults to personal user
   * campaigns; pass `sponsorActorType: "team"` with an id for team
   * campaigns (moderator-only; enforced server-side).
   */
  listMine: async (input: ListMyCampaignsInput = {}): Promise<PromotionCampaign[]> => {
    try {
      const response = await api.get<{ items: PromotionCampaign[] }>(apiRoutes.promotions.mine, {
        params: {
          sponsor_actor_type: input.sponsorActorType ?? "user",
          sponsor_actor_id: input.sponsorActorId ?? undefined,
        },
      });
      return response.data?.items ?? [];
    } catch (error) {
      return rethrowAsPromotionError(error, "Failed to load promotion campaigns.");
    }
  },

  /**
   * Soft-cancel a campaign. The remaining token budget is forfeited in
   * v1 (documented in ADR 013).
   */
  cancel: async (campaignId: number): Promise<{ cancelled: boolean }> => {
    try {
      const response = await api.post<{ cancelled: boolean }>(apiRoutes.promotions.cancel(campaignId));
      return response.data;
    } catch (error) {
      return rethrowAsPromotionError(error, "Failed to cancel promotion campaign.");
    }
  },
};
